import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { marked } from "marked";
import { ArrowLeft, CalendarDays, Eye } from "lucide-react";
import { getPost, listPosts } from "@/lib/blog.functions";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AdSlot } from "@/components/ad-slot";


/** Ambil pasangan tanya-jawab dari bagian "## FAQ" pada konten Markdown. */
function extractFaq(markdown: string) {
  const section = markdown.split(/^##\s+FAQ\s*$/im)[1];
  if (!section) return [] as { question: string; answer: string }[];
  const body = section.split(/^##\s+(?!#)/m)[0] ?? "";
  const chunks = body.split(/^###\s+/m).slice(1);
  return chunks
    .map((chunk) => {
      const [head, ...rest] = chunk.split("\n");
      const answer = rest.join(" ").replace(/[*_`>#-]/g, " ").replace(/\s+/g, " ").trim();
      return { question: (head ?? "").trim(), answer };
    })
    .filter((f) => f.question.length > 3 && f.answer.length > 10)
    .slice(0, 10);
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const post = loaderData;
    const title = post?.seo_title || post?.title || "Artikel — jasasebar";
    const description = post?.seo_description || post?.excerpt || "Panduan promosi jasa dari jasasebar.com";
    const canonical = `https://www.jasasebar.com/blog/${post?.slug ?? ""}`;
    const meta = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonical },
      { property: "og:site_name", content: "jasasebar.com" },
      { property: "article:section", content: post?.category ?? "Panduan" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    const cover = post?.cover_url
      ? post.cover_url.startsWith("http")
        ? post.cover_url
        : `https://www.jasasebar.com${post.cover_url}`
      : null;
    if (cover) {
      meta.push({ property: "og:image", content: cover });
      meta.push({ property: "og:image:alt", content: post!.title });
      meta.push({ name: "twitter:image", content: cover });
    }

    const faq = extractFaq(post?.content ?? "");
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post?.title,
          description,
          author: { "@type": "Person", name: post?.author },
          publisher: { "@type": "Organization", name: "jasasebar.com", url: "https://www.jasasebar.com" },
          mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
          datePublished: post?.published_at,
          dateModified: post?.updated_at,
          articleSection: post?.category,
          keywords: post?.tags || undefined,
          ...(cover ? { image: cover } : {}),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Beranda", item: "https://www.jasasebar.com" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.jasasebar.com/blog" },
            { "@type": "ListItem", position: 3, name: post?.title, item: canonical },
          ],
        }),
      },
    ];
    if (faq.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }),
      });
    }

    return { meta, links: [{ rel: "canonical", href: canonical }], scripts };
  },
  component: BlogPostPage,
});


function BlogPostPage() {
  const post = Route.useLoaderData();
  const html = marked.parse(post.content, { async: false }) as string;

  const listFn = useServerFn(listPosts);
  const { data: allPosts } = useQuery({ queryKey: ["blog"], queryFn: () => listFn(), staleTime: 60_000 });
  const others = (allPosts ?? []).filter((p) => p.slug !== post.slug);
  const sameCategory = others.filter((p) => p.category === post.category);
  const sidebarPosts = [...sameCategory, ...others.filter((p) => p.category !== post.category)].slice(0, 5);
  const relatedPosts = others.slice(0, 3);

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />

      <div className="mx-auto max-w-6xl px-4 py-8">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 gap-1.5">
          <Link to="/blog">
            <ArrowLeft className="size-4" /> Semua artikel
          </Link>
        </Button>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <article className="min-w-0">
            <Badge variant="secondary" className="text-[11px]">
              {post.category}
            </Badge>
            <h1 className="mt-2 font-display text-3xl leading-tight font-semibold sm:text-4xl">{post.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>{post.author}</span>
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="size-3.5" />
                {new Date(post.published_at ?? post.created_at).toLocaleDateString("id-ID", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <span className="inline-flex items-center gap-1">
                <Eye className="size-3.5" /> {post.views}
              </span>
            </div>

            {post.cover_url && (
              <img
                src={post.cover_url}
                alt={post.title}
                className="mt-6 w-full rounded-2xl border border-border object-cover"
              />
            )}

            <div className="prose-blog mt-8" dangerouslySetInnerHTML={{ __html: html }} />

            <AdSlot className="mt-8" label="Iklan" />

            {post.tags && (
              <div className="mt-8 flex flex-wrap gap-2">
                {post.tags
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .map((t) => (
                    <Badge key={t} variant="outline" className="text-[11px]">
                      #{t}
                    </Badge>
                  ))}
              </div>
            )}
          </article>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <AdSlot format="rectangle" label="Iklan" />
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm">Artikel lainnya</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 p-3 pt-0">
                {sidebarPosts.length === 0 && (
                  <p className="px-1 py-3 text-xs text-muted-foreground">Belum ada artikel lain.</p>
                )}
                {sidebarPosts.map((p, i) => (
                  <div key={p.id}>
                    {i > 0 && <Separator className="my-1 opacity-60" />}
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="flex gap-3 rounded-lg p-2 transition-colors hover:bg-accent"
                    >
                      {p.cover_url && (
                        <img
                          src={p.cover_url}
                          alt={p.title}
                          loading="lazy"
                          className="size-12 shrink-0 rounded-md border border-border object-cover"
                        />
                      )}
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium leading-snug">{p.title}</span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">{p.category}</span>
                      </span>
                    </Link>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm">Sebar jasa Anda</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="text-xs text-muted-foreground">
                  Naikkan peringkat jasa Anda di papan promosi live jasasebar.com.
                </p>
                <Button asChild size="sm" className="mt-3 w-full">
                  <Link to="/" hash="daftar">
                    Daftar sekarang
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>

        {relatedPosts.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-xl font-semibold tracking-tight">Baca juga</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((p) => (
                <Card key={p.id} className="overflow-hidden py-0">
                  <Link to="/blog/$slug" params={{ slug: p.slug }} className="block">
                    {p.cover_url && (
                      <img src={p.cover_url} alt={p.title} loading="lazy" className="h-36 w-full object-cover" />
                    )}
                    <div className="p-4">
                      <Badge variant="secondary" className="text-[11px]">
                        {p.category}
                      </Badge>
                      <h3 className="mt-2 line-clamp-2 font-display text-base leading-snug font-semibold">
                        {p.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.excerpt}</p>
                    </div>
                  </Link>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}

